import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list", "emptyState", "daySelect", "dayType", "daypart", "startDate", "endDate", "itemTemplate", "emptyTemplate"]

  static values = {
    dayLabels: { type: Object, default: {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday"
    }},
    partTimeLabel: { type: String, default: "Part-time" },
    protectedLabel: { type: String, default: "Protected" },
    externalLabel: { type: String, default: "External" },
    daypartLabels: { type: Object, default: {} },
    validationMessage: { type: String, default: "Please select a day and start date" },
    indefiniteLabel: { type: String, default: "Indefinite" }
  }

  add(event) {
    event.preventDefault()

    const dayOfWeek = this.daySelectTarget.value
    const startDate = this.startDateTarget.value
    const endDate = this.endDateTarget.value || ""
    const dayType = this.hasDayTypeTarget ? this.dayTypeTarget.value : "protected"
    const daypart = this.hasDaypartTarget ? this.daypartTarget.value : ""

    if (!dayOfWeek || !startDate) {
      alert(this.validationMessageValue)
      return
    }

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.remove()
    }

    const index = this.listTarget.querySelectorAll("[data-protected-day-item]").length
    const item = this.buildItemFromTemplate(index, dayOfWeek, startDate, endDate, dayType, daypart)
    this.listTarget.appendChild(item)
    this.resetForm()
  }

  remove(event) {
    event.preventDefault()
    const item = event.currentTarget.closest("[data-protected-day-item]")

    if (item) {
      item.remove()
      this.renumberItems()
      this.showEmptyStateIfNeeded()
    }
  }

  // Private

  buildItemFromTemplate(index, dayOfWeek, startDate, endDate, dayType, daypart) {
    const item = this.itemTemplateTarget.content.cloneNode(true).firstElementChild
    item.setAttribute("data-protected-day-item", index)

    const styles = {
      part_time_day: { container: ["bg-orange-50", "border-orange-200"], iconBg: "bg-orange-100", icon: "fa-clock", iconColor: "text-orange-600", badge: ["bg-orange-100", "text-orange-700"], label: this.partTimeLabelValue },
      external: { container: ["bg-blue-50", "border-blue-200"], iconBg: "bg-blue-100", icon: "fa-arrow-up-right-from-square", iconColor: "text-blue-600", badge: ["bg-blue-100", "text-blue-700"], label: this.externalLabelValue }
    }
    const style = styles[dayType] || { container: ["bg-red-50", "border-red-200"], iconBg: "bg-red-100", icon: "fa-shield-alt", iconColor: "text-red-600", badge: ["bg-red-100", "text-red-700"], label: this.protectedLabelValue }

    item.querySelector("[data-label]").textContent = this.dayLabelsValue[dayOfWeek] || dayOfWeek
    item.querySelector("[data-date-range]").textContent = this.formatDateRange(startDate, endDate)

    const container = item.querySelector("[data-day-type-container]") || item
    container.classList.add(...style.container)

    const iconContainer = item.querySelector("[data-icon-container]")
    if (iconContainer) {
      iconContainer.classList.add(style.iconBg)
      const icon = iconContainer.querySelector("[data-icon]")
      if (icon) {
        icon.classList.add(style.icon, style.iconColor)
      }
    }

    const badge = item.querySelector("[data-type-badge]")
    if (badge) {
      badge.classList.add(...style.badge)
      badge.textContent = style.label
    }

    const daypartBadge = item.querySelector("[data-daypart-badge]")
    if (daypartBadge && daypart) {
      daypartBadge.textContent = this.daypartLabelsValue[daypart] || daypart
      daypartBadge.classList.remove("hidden")
    }

    const fields = {
      day_of_week: dayOfWeek,
      start_date: startDate,
      end_date: endDate,
      day_type: dayType,
      daypart: daypart
    }
    item.querySelectorAll("input[data-field]").forEach(input => {
      const field = input.dataset.field
      input.name = `staff_preference[protected_days_array][${index}][${field}]`
      input.value = fields[field] || ""
      if (field === "daypart" && !daypart) input.remove()
    })

    return item
  }

  formatDateRange(startDate, endDate) {
    const options = { month: "short", day: "numeric", year: "numeric" }
    const start = new Date(startDate).toLocaleDateString("en-US", options)
    if (endDate) {
      const end = new Date(endDate).toLocaleDateString("en-US", options)
      return `${start} - ${end}`
    }
    return `${start} - ${this.indefiniteLabelValue}`
  }

  renumberItems() {
    const items = this.listTarget.querySelectorAll("[data-protected-day-item]")

    items.forEach((item, newIndex) => {
      item.setAttribute("data-protected-day-item", newIndex)
      item.querySelectorAll("input[type='hidden']").forEach(input => {
        input.name = input.name.replace(/\[\d+\]/, `[${newIndex}]`)
      })
    })
  }

  showEmptyStateIfNeeded() {
    if (this.listTarget.querySelectorAll("[data-protected-day-item]").length === 0) {
      const emptyState = this.emptyTemplateTarget.content.cloneNode(true).firstElementChild
      this.listTarget.appendChild(emptyState)
    }
  }

  resetForm() {
    this.daySelectTarget.value = ""
    if (this.hasDayTypeTarget) this.dayTypeTarget.value = "protected"
    if (this.hasDaypartTarget) this.daypartTarget.value = ""
    this.startDateTarget.value = new Date().toISOString().split("T")[0]
    this.endDateTarget.value = ""
  }
}
